import { BaseModule } from '@/modules/common.module';
import {
  ApiKey,
  ApiKeyResponse,
  BulkInviteUsers,
  ConfirmNewPassword,
  ConfirmSignIn,
  ConfirmUserSignup,
  CreateApiKey,
  CreateApiKeyResponse,
  CreateTeam,
  CreateUser,
  FindTeamsQueryParams,
  FindUserApiKeysResponse,
  OrganizationMembership,
  OrganizationMembershipListResponse,
  OrganizationMembershipResponse,
  RefreshTokens,
  SendForgotPasswordCode,
  SetPreferredMfa,
  SignIn,
  Team,
  TeamListResponse,
  TeamResponse,
  UpdateOrganizationMembership,
  UpdateTeam,
  UpdateUser,
  User,
  UserAuthResponse,
  UserResponse,
  VerifyEmail,
  VerifyPhoneNumber,
} from '@/models/user.models';
import { ApiResponseBase, PaginatedQueryBase } from '@/models/common.models';

export class UserModule extends BaseModule {
  constructor() {
    super('v1/users');
  }

  async me(): Promise<User> {
    const userResponse = await this.get<UserResponse>('me');
    return userResponse.data.data;
  }

  async updateMe(user: UpdateUser): Promise<User> {
    const userResponse = await this.patch<UserResponse>('me', user);
    return userResponse.data.data;
  }

  async signIn(signIn: SignIn): Promise<UserAuthResponse> {
    const userResponse = await this.post<UserAuthResponse>(
      'sign-in',
      signIn,
      undefined,
      1 // No retries
    );
    return userResponse.data;
  }

  async confirmSignIn(confirmSignIn: ConfirmSignIn): Promise<UserAuthResponse> {
    const userResponse = await this.post<UserAuthResponse>(
      'confirm-sign-in',
      confirmSignIn
    );
    return userResponse.data;
  }

  async signUp(user: CreateUser): Promise<User> {
    const userResponse = await this.post<UserResponse>('signup', user);
    return userResponse.data.data;
  }

  async confirmSignUp(confirmSignUp: ConfirmUserSignup): Promise<User> {
    const userResponse = await this.patch<UserResponse>(
      'confirm-signup',
      confirmSignUp
    );
    return userResponse.data.data;
  }

  async setMyPreferredMfa(setPreferredMfa: SetPreferredMfa): Promise<User> {
    const userResponse = await this.patch<UserResponse>(
      'me/set-preferred-mfa',
      setPreferredMfa
    );
    return userResponse.data.data;
  }

  async setupMyTOTP(): Promise<{
    code: string;
  }> {
    const userResponse = await this.post<{
      code: string;
    }>('me/setup-totp');
    return userResponse.data;
  }

  async refreshToken(token: RefreshTokens): Promise<UserAuthResponse> {
    const userResponse = await this.post<UserAuthResponse>(
      'refresh-tokens',
      token
    );
    return userResponse.data;
  }

  async verifyMyPhoneNumber(verify: VerifyPhoneNumber): Promise<User> {
    const userResponse = await this.patch<UserResponse>(
      'me/verify-phone-number',
      verify
    );
    return userResponse.data.data;
  }

  async verifyMyEmail(verify: VerifyEmail): Promise<User> {
    const userResponse = await this.patch<UserResponse>(
      'me/verify-email',
      verify
    );
    return userResponse.data.data;
  }

  async resendMyEmailVerificationCode(): Promise<boolean> {
    const userResponse = await this.post<{ success: boolean }>(
      'me/resend-email-verification-code'
    );
    return userResponse.data.success;
  }

  async resendVerificationCode(username: string): Promise<boolean> {
    const usersResponse = await this.post<{ success: boolean }>(
      'resend-verification-code',
      {
        username,
      }
    );
    return usersResponse.data.success;
  }

  async sendForgotPasswordCode(
    forgotPassword: SendForgotPasswordCode
  ): Promise<boolean> {
    const userResponse = await this.post<{ success: boolean }>(
      'send-forgot-password-code',
      forgotPassword
    );
    return userResponse.data.success;
  }

  async confirmNewPassword(
    confirmNewPassword: ConfirmNewPassword
  ): Promise<boolean> {
    const userResponse = await this.patch<{ success: boolean }>(
      'confirm-new-password',
      confirmNewPassword
    );
    return userResponse.data.success;
  }

  // async getOrganizationMembers(organizationId: string): Promise<Array<User>> {
  //   const usersResponse = await this.get<UserListResponse>('', {
  //     params: {
  //       organizationId,
  //     },
  //   });
  //   return usersResponse.data.data;
  // }

  async checkIfUserExists(
    username: string,
    ignoreInvited = false
  ): Promise<boolean> {
    const usersResponse = await this.get<{ exists: boolean }>('exists', {
      params: {
        username,
        ignoreInvited,
      },
    });
    return usersResponse.data.exists;
  }

  async submitJoinRequest(
    organizationId: string
  ): Promise<OrganizationMembership> {
    const usersResponse = await this.post<OrganizationMembership>(
      'submit-join-request',
      {
        organizationId,
      }
    );
    return usersResponse.data;
  }

  async approveJoinRequest(
    organizationId: string,
    userId: string
  ): Promise<OrganizationMembership> {
    const usersResponse = await this.patch<OrganizationMembershipResponse>(
      'approve-join-request',
      {
        organizationId,
        userId,
      }
    );
    return usersResponse.data.data;
  }

  async declineJoinRequest(
    organizationId: string,
    userId: string
  ): Promise<OrganizationMembership> {
    const usersResponse = await this.patch<OrganizationMembershipResponse>(
      'decline-join-request',
      {
        organizationId,
        userId,
      }
    );
    return usersResponse.data.data;
  }

  async approveInviteRequest(
    organizationId: string
  ): Promise<OrganizationMembership> {
    const usersResponse = await this.patch<OrganizationMembership>(
      'approve-invite-request',
      {
        organizationId,
      }
    );
    return usersResponse.data;
  }

  async declineInviteRequest(
    organizationId: string
  ): Promise<OrganizationMembership> {
    const usersResponse = await this.patch<OrganizationMembership>(
      'decline-invite-request',
      { organizationId }
    );
    return usersResponse.data;
  }

  async bulkInviteUsers(
    organizationId: string,
    users: BulkInviteUsers
  ): Promise<Array<OrganizationMembership>> {
    const usersResponse = await this.post<OrganizationMembershipListResponse>(
      `memberships/${organizationId}`,
      users
    );
    return usersResponse.data.data;
  }

  async sendInvitationEmail(
    organizationId: string,
    userId: string
  ): Promise<OrganizationMembership> {
    const invitationResponse = await this.post<OrganizationMembership>(
      `/memberships/${organizationId}/${userId}/send-invitation`
    );
    return invitationResponse.data;
  }

  async getOrganizationMemberships(
    organizationId: string
  ): Promise<ApiResponseBase<Array<OrganizationMembership>>> {
    const response = await this.get<
      ApiResponseBase<Array<OrganizationMembership>>
    >(`memberships/${organizationId}`);
    return response.data;
  }

  async getOrganizationMembershipById(
    organizationId: string,
    userId: string
  ): Promise<OrganizationMembership> {
    const membershipResponse = await this.get<OrganizationMembershipResponse>(
      `memberships/${organizationId}/${userId}`
    );
    return membershipResponse.data.data;
  }

  async updateOrganizationMembership(
    organizationId: string,
    userId: string,
    membership: UpdateOrganizationMembership
  ): Promise<OrganizationMembership> {
    const usersResponse = await this.patch<OrganizationMembershipResponse>(
      `memberships/${organizationId}/${userId}`,
      membership
    );
    return usersResponse.data.data;
  }

  async deleteOrganizationMembership(
    organizationId: string,
    userId: string
  ): Promise<OrganizationMembership> {
    const usersResponse = await this.delete<OrganizationMembershipResponse>(
      `memberships/${organizationId}/${userId}`
    );
    return usersResponse.data.data;
  }

  async createTeam(organizationId: string, team: CreateTeam): Promise<Team> {
    const teamResponse = await this.post<TeamResponse>(
      `teams/${organizationId}`,
      team
    );
    return teamResponse.data.data;
  }

  async findTeams(
    organizationId: string,
    query: FindTeamsQueryParams
  ): Promise<TeamListResponse> {
    const response = await this.get<TeamListResponse>(
      `teams/${organizationId}`,
      {
        params: { ...query },
      }
    );
    return response.data;
  }

  async getTeamById(organizationId: string, teamId: string): Promise<Team> {
    const teamResponse = await this.get<TeamResponse>(
      `teams/${organizationId}/${teamId}`
    );
    return teamResponse.data.data;
  }

  async updateTeam(
    organizationId: string,
    teamId: string,
    team: UpdateTeam
  ): Promise<Team> {
    const teamResponse = await this.patch<TeamResponse>(
      `teams/${organizationId}/${teamId}`,
      team
    );
    return teamResponse.data.data;
  }
  async unarchiveTeam(organizationId: string, teamId: string): Promise<Team> {
    const teamResponse = await this.patch<TeamResponse>(
      `teams/${organizationId}/${teamId}/unarchive`
    );
    return teamResponse.data.data;
  }

  async deleteTeam(organizationId: string, teamId: string): Promise<Team> {
    const teamResponse = await this.delete<TeamResponse>(
      `teams/${organizationId}/${teamId}`
    );
    return teamResponse.data.data;
  }

  async findUserApiKeys(
    query: PaginatedQueryBase
  ): Promise<FindUserApiKeysResponse> {
    const userApiKeysResponse = await this.get<FindUserApiKeysResponse>(
      `me/api-keys`,
      {
        params: { ...query },
      }
    );
    return userApiKeysResponse.data;
  }
  async createApiKey(
    organizationId: string,
    data: CreateApiKey
  ): Promise<CreateApiKeyResponse['data']> {
    const createApiKeyResponse = await this.post<CreateApiKeyResponse>(
      `memberships/${organizationId}/api-keys`,
      data
    );
    return createApiKeyResponse.data.data;
  }
  async deleteApiKey(organizationId: string, apiKey: string): Promise<boolean> {
    const deleteApiKeyResponse = await this.delete<{ success: boolean }>(
      `memberships/${organizationId}/api-keys/${apiKey}`
    );
    return deleteApiKeyResponse.data.success;
  }
  async updateApiKey(
    organizationId: string,
    apiKey: string,
    data: CreateApiKey
  ): Promise<ApiKey> {
    const editApiKeyResponse = await this.patch<ApiKeyResponse>(
      `memberships/${organizationId}/api-keys/${apiKey}`,
      data
    );
    return editApiKeyResponse.data.data;
  }
}
